import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { deleteFooterNotification as deleteFooterNotificationAction } from 'actions/notifications.js';
import {
  setOrUpdateAuthUser as setOrUpdateAuthUserAction,
  setOrUpdateOrgId as setOrUpdateOrgIdAction,
  setOrUpdateSelectedProduct as setOrUpdateSelectedProductAction,
} from 'actions/session.js';
import { unsetMasqueradeMode as unsetMasqueradeModeAction } from 'actions/ui.js';

import { trackingService } from 'services/tracking/TrackingService.js';

import * as ROUTES from 'constants/routes.js';
import { buildRoute } from 'constants/routes.js';

import { useFirebase } from 'components/Firebase/index.js';

import FeaturesWindow from 'components/FeaturesWindow/index.jsx';
import Navigation from './Navigation.jsx';
import NotificationFooter from 'components/NotificationFooter/index.jsx';
import NotificationSnackbar from 'components/NotificationSnackbar/index.jsx';
import NotificationModal from 'components/NotificationModal/index.jsx';
import MasqueradeBanner from 'components/MasqueradeBanner/index.jsx';
import ProductsNavigation from 'components-v3/ProductsNavigation/ProductsNavigation.jsx';
import Alert from 'components/Alert/Alert.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useProductDataOutsideRoutes } from './hooks/index.js';
import { CLIENT_PRODUCT } from 'constants/products.js';
import MobileAppModal from 'components-v3/MobileAppModal/index.jsx';
import usePendoCustomBehaviors from 'hooks/usePendoCustomBehaviors.js';
import usePendoUgcEligibility from 'hooks/usePendoUgcEligibility.js';
import { saveProductLastPath } from 'utils/productLastPath.js';
import useCurrentRouteKey from 'hooks/useCurrentRouteKey.js';
import DashboardFab from 'components-v3/DashboardFab/index.jsx';

const superadminPaths = [
  ROUTES.GLOBAL,
  ROUTES.DASHBOARD_TRANSLATIONS,
  ROUTES.SAML_PROVIDERS,
  ROUTES.CLIENTS,
  ROUTES.AI_TAGGING_RUNS,
  ROUTES.AI_TAGGING_RUN,
];
/**
 * Checks if you should exit any active masquerade session if a superadmin tries to:
 * a) navigate to a superadmin path without properly first exiting the masquerade mode
 * b) change the current org, by e.g. clicking a link or changing the org id in the URL
 * c) change the current client, by e.g. clicking a link or changing the client id in the URL
 */
const shouldExitMasqueradeMode = (masqueradeMode, pathname) => {
  const isMasqueraded = !!masqueradeMode;
  const isNavigatingToSaPath = superadminPaths.some((path) => pathname.includes(path));

  const currentlyMasquerading = masqueradeMode?.org?.id || masqueradeMode?.client?.id;
  const isChangingOrgOrClient = currentlyMasquerading && !pathname.includes(currentlyMasquerading);

  if (!isMasqueraded) return false;
  if (!isNavigatingToSaPath && !isChangingOrgOrClient) return false;

  return true;
};

const isRedirectRoute = ({ params, path, url }) => {
  if (path === ROUTES.ORG_SHOW && !params.selectedTab) return true;
  return false;
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const history = useHistory();
  const { orgId, selectedProduct, clientId } = useProductDataOutsideRoutes();
  const { pathname, search } = useLocation();
  const { features } = useFeatureContext();
  const { productsNavigationEnabled = true } = features;
  const { routeKey: pageName, params, path, url } = useCurrentRouteKey();

  const deleteFooterNotification = useCallback(() => dispatch(deleteFooterNotificationAction()), [dispatch]);
  const setOrUpdateAuthUser = useCallback((payload) => dispatch(setOrUpdateAuthUserAction(payload)), [dispatch]);
  const setOrUpdateOrgId = useCallback((payload) => dispatch(setOrUpdateOrgIdAction(payload)), [dispatch]);
  const setOrUpdateSelectedProduct = useCallback((payload) => dispatch(setOrUpdateSelectedProductAction(payload)), [dispatch]);

  const authUser = useSelector((state) => state.sessionState.authUser);
  const isSuperadmin = authUser?.isSuperadmin;
  const masqueradeMode = useSelector((state) => state.ui.masqueradeMode);

  usePendoCustomBehaviors();
  usePendoUgcEligibility();

  const [lastRoute, setLastRoute] = useState(null);

  useEffect(() => {
    setOrUpdateOrgId(orgId);
  }, [orgId, setOrUpdateOrgId]);

  useEffect(() => {
    setOrUpdateSelectedProduct(selectedProduct);
    localStorage.setItem('bambuser.latestProduct', selectedProduct);
  }, [selectedProduct, setOrUpdateSelectedProduct]);

  // Save the last visited path for the product, so that we
  // can redirect the user back to it when they switch products
  useEffect(() => {
    if (!orgId || !selectedProduct) return;
    saveProductLastPath(orgId, selectedProduct, `${pathname}${search}`);
  }, [orgId, selectedProduct, pathname, search]);

  useEffect(() => {
    if (!params && !path && !url) return;
    const currentRoute = JSON.stringify({ params, path, url });

    if (lastRoute === currentRoute) return;
    if (isRedirectRoute({ params, path, url })) return;
    trackingService.track({ eventType: 'identify' }, { GA: true, Pendo: true });
    trackingService.trackPageVisit({ params, path, pageName, url });
    deleteFooterNotification();
    setLastRoute(currentRoute);
  }, [lastRoute, params, path, pageName, deleteFooterNotification, url]);

  useEffect(() => {
    if (!shouldExitMasqueradeMode(masqueradeMode, location.pathname)) return;

    const unsetMasqueradeMode = () => dispatch(unsetMasqueradeModeAction());

    trackingService.track({
      eventType: 'masquerade:exit',
    });

    unsetMasqueradeMode();
    setOrUpdateOrgId(null);
  }, [dispatch, pathname, masqueradeMode, setOrUpdateOrgId]);

  const shouldRedirectToSuperadminLanding = useMemo(() => {
    if (!isSuperadmin) return false;
    if (masqueradeMode) return false;
    const isSuperadminPath = superadminPaths.some((superadminPath) => pathname.includes(superadminPath));
    if (isSuperadminPath) return false;
    if (authUser.membership(orgId)) return false;
    if (selectedProduct === CLIENT_PRODUCT && clientId) return false;
    // Public/unauthenticated pages must render for superadmins too — otherwise
    // a signed-in superadmin visiting e.g. /public/product-scraper gets bounced
    // to /global before the page can load.
    const isUnauthenticatedPage = ROUTES.UNAUTHENTICATED_ROUTES.some((route) => pathname.includes(route));
    if (isUnauthenticatedPage) return false;
    return true;
  }, [isSuperadmin, pathname, masqueradeMode, authUser, orgId, selectedProduct, clientId]);

  useEffect(() => {
    const onSignedIn = (updatedAuthUser) => {
      setOrUpdateAuthUser(updatedAuthUser);
      if (shouldRedirectToSuperadminLanding) history.push(ROUTES.GLOBAL);
    };

    const onSignedOut = () => {
      const isUnauthenticatedPage = ROUTES.UNAUTHENTICATED_ROUTES.some((route) => pathname.includes(route));
      if (isUnauthenticatedPage) return;

      const nextLink = `${pathname}${search}`;
      const queryParams = (nextLink && !nextLink.includes(ROUTES.SIGN_OUT) && nextLink !== ROUTES.NO_TEAM) ? { next: nextLink } : {};

      history.push(buildRoute(ROUTES.SIGN_IN, {}, queryParams));
    };

    const eventListener = firebase.onAuthUserListener(onSignedIn, onSignedOut);

    return () => {
      eventListener();
    };
  }, [firebase, history, pathname, search, setOrUpdateAuthUser, shouldRedirectToSuperadminLanding]);

  return (
    <>
      <FeaturesWindow />
      {!productsNavigationEnabled && <Navigation>{children}</Navigation>}
      {productsNavigationEnabled && <ProductsNavigation>{children}</ProductsNavigation>}
      <NotificationFooter />
      <NotificationSnackbar />
      <NotificationModal />
      <MasqueradeBanner />
      <Alert />
      <DashboardFab />
      <MobileAppModal />
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
};

export default Layout;
